import React from 'react';
import { Box, Typography } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatCurrency, formatDate } from '../../utils/formatters';

/**
 * PnLChart — Cumulative P&L line/area chart.
 * Green fill when positive, red when negative.
 */
const PnLChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No P&L data available
        </Typography>
      </Box>
    );
  }

  const lastValue = data[data.length - 1]?.value || 0;
  const chartColor = lastValue >= 0 ? '#3fb950' : '#f85149';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            boxShadow: 3,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatDate(item.date)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: item.value >= 0 ? 'success.main' : 'error.main' }}
          >
            {formatCurrency(item.value)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={chartColor} stopOpacity={0.2} />
              <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            tickFormatter={(val) => {
              const d = new Date(val);
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }}
            tick={{ fontSize: 11, fill: '#8b949e' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(val) => `$${val >= 1000 ? `${(val / 1000).toFixed(1)}K` : val}`}
            tick={{ fontSize: 11, fill: '#8b949e' }}
            axisLine={false}
            tickLine={false}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#484f58" strokeDasharray="3 3" />
          <Area
            type="monotone"
            dataKey="value"
            stroke={chartColor}
            strokeWidth={2}
            fill="url(#pnlGradient)"
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PnLChart;
